import Consts from '@/api/Consts';
import MapHelpers from '@/components/map/utils/MapHelpers';

import {
	HEATMAP_STATUS_OK,
	HEATMAP_STATUS_BREAK,
	HEATMAP_STATUS_SINGLE,
	HEATMAP_STATUS_MULTI,
	buildHeatmapColorSettings,
	SPOOFING_GNSS_CODES,
} from '@/utils/spoofingVisibilityHeatmapUtils';
import type { HeatmapTooltipRow, HeatmapChartPointItem } from '@/utils/spoofingVisibilityHeatmapUtils';


export const SPOOFING_GSS_DEFAULT_ELEVATION = 5;


export type GssMedianAggregatedItem = {
	current_datetime: string;
	mountpoint: string;
	gnss: string;
	elevation: number;
	cover_sec: number;
	median_percent: number;
};


export const formatGssMedianTooltipValue = (
	percent: number,
	coverSec: number,
	messagesLabel: string,
): string => {
	return String(percent) + '% (' + String(coverSec) + ' ' + messagesLabel + ')';
};


export const filterGssMedianAggregatedItemsByElevation = (
	items: GssMedianAggregatedItem[],
	elevation: number = SPOOFING_GSS_DEFAULT_ELEVATION,
): GssMedianAggregatedItem[] => {
	return items.filter(item => item.elevation === elevation);
};


type GnssMedianBucket = {
	coverSec: number;
	medianPercent: number;
};


const indexGssMedianBucket = (items: GssMedianAggregatedItem[]): Map<string, GnssMedianBucket> => {
	const result = new Map<string, GnssMedianBucket>();
	for (const item of items) {
		result.set(item.gnss, {
			coverSec: item.cover_sec,
			medianPercent: item.median_percent,
		});
	}
	return result;
};


export const classifyGssMedianAggregatedCell = (
	items: GssMedianAggregatedItem[],
	visibilityThreshold: number,
	messagesLabel: string,
): { status: number | null; tooltipRows: HeatmapTooltipRow[] } => {
	const filteredItems = filterGssMedianAggregatedItemsByElevation(items);
	if (filteredItems.length === 0) {
		return { status: null, tooltipRows: [] };
	}
	const index = indexGssMedianBucket(filteredItems);
	const failingGnss: string[] = [];
	const tooltipRows: HeatmapTooltipRow[] = [];
	const sortedGnss = Consts.sortGnssCodes(
		Array.from(index.keys()).filter(gnss => SPOOFING_GNSS_CODES.includes(gnss)),
	);
	for (const gnss of sortedGnss) {
		const bucket = index.get(gnss);
		if (bucket == null) {
			continue;
		}
		const gnssName = MapHelpers.getGnssDataDisplay(gnss).name;
		tooltipRows.push({
			label: gnssName,
			value: formatGssMedianTooltipValue(bucket.medianPercent, bucket.coverSec, messagesLabel),
		});
		if (bucket.medianPercent < visibilityThreshold) {
			failingGnss.push(gnss);
		}
	}
	if (tooltipRows.length === 0) {
		return { status: null, tooltipRows: [] };
	}
	const coverSec = filteredItems[0]?.cover_sec;
	if (coverSec === 0) {
		return { status: HEATMAP_STATUS_BREAK, tooltipRows };
	}
	if (failingGnss.length === 0) {
		return { status: HEATMAP_STATUS_OK, tooltipRows };
	}
	if (failingGnss.length === 1) {
		return { status: HEATMAP_STATUS_SINGLE, tooltipRows };
	}
	return { status: HEATMAP_STATUS_MULTI, tooltipRows };
};


export const buildGssMedianAggregatedChartItems = (
	items: GssMedianAggregatedItem[],
	visibilityThreshold: number,
	messagesLabel: string,
): HeatmapChartPointItem[] => {
	const byCell = new Map<string, GssMedianAggregatedItem[]>();
	for (const item of items) {
		const key = item.mountpoint + '|' + item.current_datetime;
		const existing = byCell.get(key);
		if (existing == null) {
			byCell.set(key, [item]);
		} else {
			existing.push(item);
		}
	}
	const result: HeatmapChartPointItem[] = [];
	for (const cellItems of byCell.values()) {
		const first = cellItems[0];
		if (first == null) {
			continue;
		}
		const classification = classifyGssMedianAggregatedCell(cellItems, visibilityThreshold, messagesLabel);
		result.push({
			current_datetime: first.current_datetime,
			mountpoint: first.mountpoint,
			status: classification.status,
			tooltipRows: classification.tooltipRows,
		});
	}
	return result;
};


export { buildHeatmapColorSettings };
